import React, {
	ComponentProps,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react"

import Layout from "../components/Layout/Layout"

type LayoutProps = ComponentProps<typeof Layout>

export type MenuItems = LayoutProps["menuItems"]
export type MenuItem = MenuItems[0]

interface LayoutContextInterface {
	setTitle: (title: string) => void
	setMenuItems: (
		menuItems: LayoutProps["menuItems"] | Promise<LayoutProps["menuItems"]>
	) => void
	setClassName: (className: LayoutProps["className"] | null) => void
}
const LayoutContext = createContext<LayoutContextInterface | null>(null)

export const useLayoutContext = () => {
	const ctx = useContext(LayoutContext)

	if (ctx === null) {
		throw new Error("No LayoutContextProvider available.")
	}

	return ctx
}

export const LayoutContextProvider: React.FC = ({ children }) => {
	const [title, setTitle] = useState("")
	const [menuItems, _setMenuItems] = useState<LayoutProps["menuItems"]>([])
	const [className, setClassName] =
		useState<LayoutProps["className"]>(undefined)
	const mounted = useRef(true)

	useEffect(() => {
		mounted.current = true

		return () => {
			mounted.current = false
		}
	}, [])

	const setClassNameCb = useCallback(
		(className: LayoutProps["className"] | null) => {
			if (className === null) {
				return
			}

			setClassName(className)
		},
		[]
	)

	const setMenuItems = useCallback(
		async (
			items: LayoutProps["menuItems"] | Promise<LayoutProps["menuItems"]>
		) => {
			const resolvedItems = await items

			if (!mounted.current) {
				return
			}

			_setMenuItems(resolvedItems)
		},
		[]
	)

	const ctx = {
		setTitle,
		setMenuItems,
		setClassName: setClassNameCb,
	}

	return (
		<LayoutContext.Provider value={ctx}>
			<Layout title={title} menuItems={menuItems} className={className}>
				{children}
			</Layout>
		</LayoutContext.Provider>
	)
}
